import { fabric } from 'fabric';
import { v4 as uuidv4 } from 'uuid';

import { useEditorData } from '../../lib/FabricJs';
import { useSlideSave } from '../../lib/FabricJs/providers/SaveProvider';
import { IFRAME_WIDGETS } from '../../config';
import { startVideoRenderLoop } from '../../lib/FabricJs/utils/handleVideo';
import { getDateAndTimeSource } from '../../lib/FabricJs/utils/handleWorldClockAndDate';

const restoreSelection = (
  canvas: fabric.Canvas,
  previousSelection: fabric.Object[],
) => {
  const objects = canvas.getObjects();
  const selection = previousSelection.filter((object) =>
    objects.includes(object),
  );
  if (selection.length === 1) {
    canvas.setActiveObject(selection[0]);
  } else if (selection.length > 1) {
    canvas.setActiveObject(new fabric.ActiveSelection(selection, { canvas }));
  }
};

export const useUpdateCanvas = () => {
  const { setActiveColor } = useEditorData();
  const { markLoaded, markChanged } = useSlideSave();

  type PresentType = { backgroundColor: string } & common.ProjectDataHistory[];

  // Only undo/redo needs the canvas rebuilt from the snapshot; after a normal
  // edit the canvas already matches it.
  const updateCanvas = (
    canvas: fabric.Canvas,
    present: PresentType,
    isUndoRedo: boolean,
    isReset: boolean,
  ) => {
    try {
      if (present.backgroundColor) {
        canvas?.setBackgroundColor(present.backgroundColor, () => {});
        canvas?.requestRenderAll();
        setActiveColor(present.backgroundColor);
      } else {
        const previousSelection = isUndoRedo ? canvas.getActiveObjects() : [];
        if (isUndoRedo) {
          // A multi-selection would keep drawing its objects relative to
          // itself after they are restored to slide coordinates.
          canvas.discardActiveObject();
          canvas._objects.splice(0, canvas._objects.length);
        }
        present.map(({ object, data }) => {
          const widgetType = data?.data?.type;
          if (
            Object.values(IFRAME_WIDGETS).includes(widgetType) &&
            data?.data?.className
          ) {
            const elements = document.getElementsByClassName(
              data?.data?.className,
            );
            const elementsArray = Array.prototype.slice.call(elements);
            const ratio = canvas?.getZoom() || 1;
            const scaledWidth = `${data.width * data.scaleX * ratio}px`;
            const scaledHeight = `${data.height * data.scaleY * ratio}px`;
            const objectLeft = `${data.left * ratio}px`;
            const objectTop = `${data.top * ratio}px`;
            elementsArray.forEach((element: HTMLElement) => {
              const resized =
                element.style.width !== scaledWidth ||
                element.style.height !== scaledHeight;
              element.style.left = objectLeft;
              element.style.top = objectTop;
              element.style.width = scaledWidth;
              element.style.height = scaledHeight;
              element.draggable = !data.lockMovementX;
              // Clock/date content is laid out in pixels.
              if (
                resized &&
                element instanceof HTMLIFrameElement &&
                (widgetType === IFRAME_WIDGETS.WORLD_CLOCK ||
                  widgetType === IFRAME_WIDGETS.DATE)
              ) {
                element.srcdoc = getDateAndTimeSource(
                  Intl.DateTimeFormat().resolvedOptions().timeZone,
                  scaledWidth,
                  scaledHeight,
                  data.data.color || '#000000',
                  widgetType === IFRAME_WIDGETS.WORLD_CLOCK,
                  true,
                );
              }
            });
          }
          // Ignore duplicate objects.
          if (isUndoRedo && !object?.isDelete) {
            object.set({
              id: object?.id || uuidv4(),
              ...data,
              // Snapshots of objects without `data` have no such key, so
              // `data` added later (e.g. a layer name) must be removed here.
              ...(!('data' in data) && { data: undefined }),
              auto: true, // Identify Automatically added objects.
            });
            canvas.add(object);
          }
        });
        if (isUndoRedo) {
          restoreSelection(canvas, previousSelection);
          canvas.requestRenderAll();
          // Undo can re-add a video that is still playing (no new 'play' event).
          startVideoRenderLoop(canvas);
        }
      }
      // A reset marks the freshly loaded slide as the starting point; edits
      // after it are compared with the saved slide.
      if (isReset) {
        markLoaded(canvas);
        return;
      }
      markChanged(canvas);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('Ignore error', err);
    }
  };
  return { updateCanvas };
};
